/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
    ComponentRef,
    Injectable,
    ViewContainerRef
} from '@angular/core';
import {
    PartNumberChangesDetailFooterConfigurationModel
} from '../part-number-changes-detail-configuration/models/part-number-changes-detail-footer-configuration.model';
import {
    PartNumberChangesDetailFooterComponent
} from '../../part-number-changes-footer/part-number-changes-detail-footer.component';
import {PartNumberChangesDetailFooterActionsEnum} from '../../models/part-number-changes-detail-footer-actions.enum';
import {
    PartNumberChangesDetailSolutionComponent
} from '../../part-number-changes-solution/part-number-changes-detail-solution.component';
import {
    PartNumberChangesDetailSolutionState
} from '../../part-number-changes-solution/part-number-changes-detail-solution.state';
import {
    PartNumberChangesDetailTrialConfigurationModel
} from '../part-number-changes-detail-configuration/models/pnc-trial/part-number-changes-detail-trial-configuration.model';
import {
    PartNumberChangesOldCredentialsComponent
} from '../../part-number-changes-old-credentials/part-number-changes-old-credentials.component';
import {
    PartNumberChangesCredentialsComponent
} from '../../part-number-changes-credentials/part-number-changes-credentials.component';
import {
    PartNumberChangesDetailTitleComponent
} from '../../part-number-changes-title/part-number-changes-detail-title.component';
import {
    PartNumberChangesDetailHeaderConfigurationModel
} from '../part-number-changes-detail-configuration/models/part-number-changes-detail-header-configuration.model';
import {
    PartNumberChangesDetailHeaderComponent
} from '../../part-number-changes-header/part-number-changes-detail-header.component';
import {
    PartNumberChangesDetailComponentsModel
} from '../part-number-changes-detail-configuration/models/part-number-changes-detail-components.model';
import {
    Subject,
    Subscription,
    timer
} from 'rxjs';
import {
    PartNumberChangesCredentialsStateModel
} from '../../part-number-changes-credentials/part-number-changes-credentials-state.model';
import {RouteSearchCriteriaModel} from '../../../api/ms-partnumberchanges/contracts/route-search-criteria.model';
import {PartNumberChangesOldCredentialsFormState} from '../../models/part-number-changes-old-credentials-form.state';
import {
    PartNumberChangesSolutionConfigurationModel
} from '../part-number-changes-detail-configuration/models/pnc-trial/part-number-changes-solution-configuration.model';
import {
    PartNumberChangesDetailStickyHeaderConfigurationModel
} from '../part-number-changes-detail-configuration/models/part-number-changes-detail-sticky-header-configuration.model';
import {
    PartNumberChangesDetailStickyHeaderComponent
} from '../../part-number-changes-sticky-header/part-number-changes-detail-sticky-header.component';
import {
    PartNumberChangesCredentialsSectionConfigurationModel
} from '../part-number-changes-detail-configuration/models/part-number-changes-credentials-section-configuration.model';
import {
    PartNumberChangesDialogButtonComponent
} from '../../part-number-changes-dialog-button/part-number-changes-dialog-button.component';
import {
    PartNumberChangesAiSolutionComponent
} from '../../part-number-changes-ai-solution/part-number-changes-ai-solution.component';
import {
    PartNumberChangesAiSolutionState
} from '../../part-number-changes-ai-solution/part-number-changes-ai-solution.state';
import {PartNumberChangesStatusEnum} from '../../models/part-number-changes-status.enum';
import {PncDetailModel} from '../../models/pnc-detail/pnc-detail.model';
import {
    PartNumberChangesUpdateAiMaterialModel
} from '../../part-number-changes-ai-solution/models/part-number-changes-update-ai-material.model';
import {
    PartNumberChangesCancellationConfigurationModel
} from '../part-number-changes-detail-configuration/models/cancellation/part-number-changes-cancellation-configuration.model';
import {
    PartNumberChangesCancellationComponent
} from '../../part-number-changes-cancellation/part-number-changes-cancellation.component';
import {
    PartNumberChangeDetailSolutionTrialConfigurationModel
// eslint-disable-next-line max-len
} from '../part-number-changes-detail-configuration/models/pnc-solution-trials/part-number-change-detail-solution-trial-configuration.model';
import {
    PartNumberChangeDetailSolutionTrialComponent
} from '../../part-number-changes-solution/part-number-change-detail-solution-trial/part-number-change-detail-solution-trial.component';
import {ButtonDirectionEnum} from './button-direction.enum';
import {SupplyingPackingPlantBaseModel} from "../../models/supplying-packing-plant/supplying-packing-plant-base.model";
import {PncTypeEnum} from "../../models/pnc-type-enum/pnc-type.enum";

@Injectable({
    providedIn: 'root'
})
export class PartNumberChangesCreateComponentsService {
    private createdComponents: ComponentRef<any>[] = [];
    private subscriptions: Subscription = new Subscription;

    constructor() {
    }

    get createdComponentsData(): ComponentRef<any>[] {
        return this.createdComponents;
    }

    updateComponents(create: (components: PartNumberChangesDetailComponentsModel[]) => void,
                     components: PartNumberChangesDetailComponentsModel[]): void {
        if (this.createdComponents?.length > 0) {
            this.destroyComponents();
        }
        create(components);
    }

    updateComponentsWithTimer(create: (components: PartNumberChangesDetailComponentsModel[]) => void,
                              components: PartNumberChangesDetailComponentsModel[]): void {
        if (this.createdComponents?.length > 0) {
            this.destroyComponents();
        }
        // TODO: CTWILS-6002 - Chrome forces scroll up when destroying and creating components right after.
        //  Timer queues a new task on the event loop to be addressed later.
        //  The timer's dueTime gives enough time for the browser to render the components destruction, and then subsequent construction.
        timer(0)
                .subscribe(() => create(components));
    }

    updateTrialsComponents(showTrials: boolean, pncDetail: PncDetailModel): void {
        let trialsCounter = 0;
        this.createdComponents.forEach((component) => {
            if (component.componentType === PartNumberChangesOldCredentialsComponent) {
                // If it has routeSearchCriteria it is the current trial and must always be displayed
                if (component.instance.routeSearchCriteria) {
                    component.instance.isHidden = false;
                } else {
                    trialsCounter++;
                    // Expose the latest rejected trial
                    if (this.showTrialsBasedOnPNCStatusCode(pncDetail.status.code) && trialsCounter === pncDetail.trials.length) {
                        component.instance.isHidden = false;
                    } else {
                        // Old trials should be conditionally render according to showTrials
                        component.instance.isHidden = !showTrials;
                    }
                }
            } else if (component.componentType === PartNumberChangesDialogButtonComponent) {
                component.instance.isDialogOpen = showTrials;
            }
        });
    }

    destroyComponents(): void {
        this.createdComponents.forEach((component) => {
            component.destroy();
        });
        this.createdComponents = [];
    }

    mapStickyHeaderSection(value: PartNumberChangesDetailStickyHeaderConfigurationModel, container: ViewContainerRef,
                           doneAction: (action: boolean) => void): void {
        const component = container.createComponent(PartNumberChangesDetailStickyHeaderComponent);

        component.instance.configuration = value;

        this.subscriptions.add(component.instance.done?.subscribe((action: boolean) => doneAction(action)));
        this.createdComponents.push(component);
    }

    mapHeaderSection(value: PartNumberChangesDetailHeaderConfigurationModel, container: ViewContainerRef): void {
        const component = container.createComponent(PartNumberChangesDetailHeaderComponent);

        component.instance.configuration = value;

        this.createdComponents.push(component);
    }

    mapTitleSection(value: string, container: ViewContainerRef): void {
        const component = container.createComponent(PartNumberChangesDetailTitleComponent);

        component.instance.sectionTitle = value;

        this.createdComponents.push(component);
    }

    mapCredentialsSection(value: PartNumberChangesCredentialsSectionConfigurationModel,
                          container: ViewContainerRef,
                          routeSearchCriteriaChange?: (searchCriteria: RouteSearchCriteriaModel) => void,
                          isLoading?: (loading: boolean) => void,
                          newCredentials?: (credentials: PartNumberChangesCredentialsStateModel) => void,
                          isValid?: (valid: boolean) => void,
                          resetForm?: Subject<void>): void {
        const component = container.createComponent(PartNumberChangesCredentialsComponent);

        component.instance.credentials = value.credentials;
        component.instance.sectionTitle = value.sectionTitle;
        component.instance.statusTimeline = value.statusTimelineConfiguration;
        component.instance.pncType = value?.pncType;

        if (routeSearchCriteriaChange != null) {
            this.subscriptions.add(component.instance.routeSearchCriteriaChange
                    .subscribe((searchCriteria: RouteSearchCriteriaModel) => routeSearchCriteriaChange(searchCriteria)));
        }

        if (isLoading != null) {
            this.subscriptions.add(component.instance.isLoading.subscribe((loading: boolean) => isLoading(loading)));
        }

        if (newCredentials != null) {
            this.subscriptions.add(component.instance.partNumberChangesCredentials
                    .subscribe((credentials: PartNumberChangesCredentialsStateModel) => newCredentials(credentials)));
        }

        if (isValid != null) {
            this.subscriptions.add(component.instance.validForCreate.subscribe((valid: boolean) => isValid(valid)));
        }

        if (resetForm != null) {
            component.instance.triggerResetForm = resetForm;
        }

        this.createdComponents.push(component);
    }

    mapDialogHistoryButtonSection(container: ViewContainerRef,
                                  dialogButtonTitle: string,
                                  dialogStatus: boolean,
                                  dialogHistoryAction: () => void,
                                  dialogButtonDirection: ButtonDirectionEnum): void {
        const component = container.createComponent(PartNumberChangesDialogButtonComponent);

        component.instance.isDialogOpen = dialogStatus;
        component.instance.dialogButtonTitle = dialogButtonTitle;
        component.instance.dialogButtonDirection = dialogButtonDirection;
        this.subscriptions.add(component.instance.toggleDialog.subscribe(() => {
            dialogHistoryAction();
        }));

        this.createdComponents.push(component);
    }

    mapTrialsSection(value: PartNumberChangesDetailTrialConfigurationModel[],
                     container: ViewContainerRef,
                     pncStatus: string,
                     showTrials: boolean): void {
        const numberOfTrials = value.length;

        value.forEach((trial, index) => {
            const component = container.createComponent(PartNumberChangesOldCredentialsComponent);

            component.instance.oldPartNumberCredentials = trial;

            // Should only hide trials when there are more than 2
            if (numberOfTrials < 2) {
                component.instance.isHidden = false;
            } else {
                // Expose the latest rejected trial
                if (this.showTrialsBasedOnPNCStatusCode(pncStatus) && index === value.length - 1) {
                    component.instance.isHidden = false;
                } else {
                    // Hide by default the trials
                    component.instance.isHidden = !showTrials;
                }
            }

            this.createdComponents.push(component);
        });
    }

    mapSolutionTrialsSection(value: PartNumberChangeDetailSolutionTrialConfigurationModel[],
                             container: ViewContainerRef): void {

        value.forEach((trial) => {
            const component = container.createComponent(PartNumberChangeDetailSolutionTrialComponent);

            component.instance.solutionTrials = trial;
            this.createdComponents.push(component);
        });
    }

    mapOldPartNumber(value: PartNumberChangesDetailTrialConfigurationModel,
                     container: ViewContainerRef,
                     oldMaterialChange: (material: PartNumberChangesUpdateAiMaterialModel) => void,
                     routeSearchCriteriaChange: Subject<RouteSearchCriteriaModel>,
                     isValid: (valid: boolean) => void,
                     newOldPartNumber: (newPartNumber: PartNumberChangesOldCredentialsFormState) => void,
                     isLoading?: (loading: boolean) => void,
                     resetForm?: Subject<void>): void {
        const component = container.createComponent(PartNumberChangesOldCredentialsComponent);

        component.instance.oldPartNumberCredentials = value;
        component.instance.updateRouteSearchCriteriaChange = routeSearchCriteriaChange;
        component.instance.isLoading = isLoading;

        if (oldMaterialChange != null) {
            this.subscriptions.add(component.instance.oldMaterialChange
                    .subscribe((materialAiChange: PartNumberChangesUpdateAiMaterialModel) => oldMaterialChange(materialAiChange)));
        }
        this.subscriptions.add(component.instance.validForCreate
                .subscribe((valid: boolean) => isValid(valid)));
        this.subscriptions.add(component.instance.partNumberChangesOldPartNumberCredentials
                .subscribe((newPartNumber: PartNumberChangesOldCredentialsFormState) => newOldPartNumber(newPartNumber)));

        if (resetForm != null) {
            component.instance.triggerFormReset = resetForm;
        }
        this.createdComponents.push(component);
    }

    mapCurrentTrialSection(value: PartNumberChangesDetailTrialConfigurationModel,
                           container: ViewContainerRef,
                           isValid: (valid: boolean) => void,
                           mapTrial: (trial: PartNumberChangesOldCredentialsFormState) => void,
                           routeSearchCriteria: RouteSearchCriteriaModel,
                           trialTitle?: (trialTitle: string) => void): void {
        const component = container.createComponent(PartNumberChangesOldCredentialsComponent);

        component.instance.oldPartNumberCredentials = value;
        component.instance.routeSearchCriteria = routeSearchCriteria;
        this.subscriptions.add(component.instance.validForCreate.subscribe((valid: boolean) => isValid(valid)));
        this.subscriptions.add(component.instance.partNumberChangesOldPartNumberCredentials
                .subscribe((trial: PartNumberChangesOldCredentialsFormState) => mapTrial(trial)));
        this.subscriptions.add(component.instance?.titleTrial?.subscribe((title: string) => trialTitle(title)));

        this.createdComponents.push(component);
    }

    mapSolutionSection(value: PartNumberChangesSolutionConfigurationModel,
                       container: ViewContainerRef,
                       supplyingPlantCode: string,
                       solutionSupplyingPlant: SupplyingPackingPlantBaseModel,
                       alternativeSupplyingPackingPlants: SupplyingPackingPlantBaseModel[],
                       mapSolution: (solution: PartNumberChangesDetailSolutionState) => void,
                       routeSearchCriteriaChange?: Subject<RouteSearchCriteriaModel>,
                       resetForm?: Subject<void>,
                       titleTrial?: number,
                       showSolutionSpecialist?: boolean): void {
        const component = container.createComponent(PartNumberChangesDetailSolutionComponent);

        component.instance.supplyingPackingPlantCode = supplyingPlantCode;
        component.instance.solutionSupplyingPackingPlant = solutionSupplyingPlant;
        component.instance.alternativeSupplyingPackingPlants = alternativeSupplyingPackingPlants;
        component.instance.isPncTypePartnerOrPlanner = value?.type?.code === PncTypeEnum.PLANNER || value?.type?.code === PncTypeEnum.PARTNER;
        component.instance.configuration = value;
        component.instance.updateRouteSearchCriteriaChange = routeSearchCriteriaChange;
        this.subscriptions.add(component.instance.solution
                .subscribe((solution: PartNumberChangesDetailSolutionState) => {
                    mapSolution(solution);
                }));
        if (resetForm != null) {
            component.instance.triggerFormReset = resetForm;
        }

        if (titleTrial != null) {
            component.instance.solutionTrialNumber = titleTrial + 1;
        }

        if (showSolutionSpecialist != null) {
            component.instance.showSolutionSpecialist = showSolutionSpecialist;
        }

        this.createdComponents.push(component);
    }

    mapAiSolutionSection(value: PartNumberChangesSolutionConfigurationModel,
                         container: ViewContainerRef,
                         supplyingPlantCode: string,
                         mapSolution: (solution: PartNumberChangesAiSolutionState) => void,
                         routeSearchCriteriaChange?: Subject<RouteSearchCriteriaModel>,
                         oldPartNumberMaterialChange?: Subject<PartNumberChangesUpdateAiMaterialModel>,
                         isLoading?: (loading: boolean) => void,
                         resetForm?: Subject<void>): void {
        const component = container.createComponent(PartNumberChangesAiSolutionComponent);

        component.instance.supplyingPackingPlantCode = supplyingPlantCode;
        component.instance.configuration = value;
        component.instance.updateRouteSearchCriteriaChange = routeSearchCriteriaChange;
        component.instance.updatedOldPartNumberMaterial = oldPartNumberMaterialChange;
        component.instance.isLoading = isLoading;

        this.subscriptions.add(component.instance.solution
                .subscribe((solution: PartNumberChangesAiSolutionState) => mapSolution(solution)));

        if (resetForm != null) {
            component.instance.triggerFormReset = resetForm;
        }

        this.createdComponents.push(component);
    }

    mapFooterSection(value: PartNumberChangesDetailFooterConfigurationModel,
                     container: ViewContainerRef,
                     footerAction: (action: PartNumberChangesDetailFooterActionsEnum) => void): void {
        const component = container.createComponent(PartNumberChangesDetailFooterComponent);

        component.instance.configuration = value;
        this.subscriptions.add(component.instance.action
                .subscribe((action: PartNumberChangesDetailFooterActionsEnum) => footerAction(action)));

        this.createdComponents.push(component);
    }

    updateFooterSection(value: PartNumberChangesDetailFooterConfigurationModel): void {
        const component = this.createdComponents.find((componentUpdate) =>
                componentUpdate.componentType === PartNumberChangesDetailFooterComponent);
        if (component != null) {
            component.instance.configuration = value;
        }
    }

    mapCancelledSection(value: PartNumberChangesCancellationConfigurationModel,
                        container: ViewContainerRef): void {
        const component = container.createComponent(PartNumberChangesCancellationComponent);

        component.instance.configuration = value;

        this.createdComponents.push(component);
    }

    private showTrialsBasedOnPNCStatusCode(status: string): boolean {
        return status === PartNumberChangesStatusEnum.REJECTED
                || status === PartNumberChangesStatusEnum.CANCELLED
                || status === PartNumberChangesStatusEnum.EE_ANALYSIS;
    }
}
